import { Alert } from "@/components/Alert"
import { Button } from "@/components/Button"
import { Modal } from "@/components/Modal"
import { isRainProvider } from "@/constants/ProviderConstants"
import { useNavigate } from "@/hooks/useNavigate"
import { ReactComponent as CopyIcon } from "@/Images/icons/Copy.svg"
import { ReactComponent as OnRampIcon } from "@/Images/icons/OnRamp.svg"
import { cn } from "@/utils"
import { components, DepositType, useInstruction, useTopUpStore } from "@usher/pe-client-front-end-lib"
import { AlertTriangle } from "lucide-react"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useHistory, useLocation } from "react-router-dom"
import "./style.sass"

const Instruction = ({ depositType, withSendHash = false, isOrder }: { depositType?: DepositType; withSendHash?: boolean; isOrder?: boolean }) => {
  const { state } = useLocation()
  const provider = (state as { provider_id?: string })?.provider_id
  const { $t: t } = useIntl()
  const navigate = useNavigate()
  const history = useHistory()
  const block = useRef(true)
  const isIban = depositType === DepositType.IBAN
  const isCard = depositType === DepositType.Card
  const isOrderIban = isIban && isOrder
  const [showDialog, setShowDialog] = useState("")
  const { getCryptoAmounts, card } = useTopUpStore((s) => ({ getCryptoAmounts: s.getCryptoAmounts, card: s.card }))
  const isExactProvider = isRainProvider(isOrder ? provider : card?.provider_id)
  const isRainCard = isRainProvider(useTopUpStore((state) => state.card)?.provider_id)
  const { fetchDepositAddress, createDeposit, createLoading, QRCode, crypto, deposit, fiat, wallet, copyToClipboard, loading, isOnrampUser } =
    useInstruction({ depositType, withSendHash, isOrder })

  const amount = useMemo(() => {
    const { amountCrypto, commissionCrypto } = getCryptoAmounts(deposit, crypto, fiat)
    const isBTC = crypto === "BTC"
    const fraction = isBTC ? 8 : 5
    if (isCard || isOrder) return amountCrypto.toFixed(fraction)
    return (amountCrypto + commissionCrypto).toFixed(fraction)
  }, [crypto, isOrder, deposit, fiat, getCryptoAmounts, isCard])

  const unblock = history.block((params) => {
    if (isOnrampUser) return true as unknown as void
    setShowDialog(params.pathname)
    if (block.current) return false
    return true as unknown as void
  })

  const onleave = (e: FocusEvent) => {
    e.preventDefault()
  }
  const onUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault()
  }
  useEffect(() => {
    if (!isOnrampUser) {
      window.addEventListener("blur", onleave)
      window.addEventListener("beforeunload", onUnload)
    }
    return () => {
      unblock()
      window.removeEventListener("blur", onleave)
      window.removeEventListener("beforeunload", onUnload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isNaN(Number(amount))) {
      let backUrl = "/topup/by-crypto"
      if (isCard) backUrl = "/cards/create"
      if (isIban) backUrl = "/iban/order"
      window.removeEventListener("blur", onleave)
      block.current = false
      navigate(backUrl, { keepSearch: true, keepState: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, isCard])

  return (
    <>
      <Modal title={t({ id: "do-you-want" })} show={Boolean(showDialog)} onClose={() => setShowDialog("")}>
        <div className="max-w-[500px] mt-3">
          <Alert>
            <span className="font-normal">
              <FormattedMessage
                id="tu-attention"
                values={{
                  b: (chunk) => <strong className="font-[ChakraPetchSemiBold]">{chunk}</strong>,
                }}
              />
            </span>
          </Alert>
          <div className="float-right flex gap-2.5 mt-4">
            <Button onClick={() => setShowDialog("")} variant="outlined">
              <FormattedMessage id="go-back-confirm" />
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                block.current = false
                navigate(showDialog, { keepSearch: true, keepState: true })
              }}
            >
              <FormattedMessage id="leave-page" />
            </Button>
          </div>
        </div>
      </Modal>
      {isIban && isOrder && (
        <div className={cn("p-4", "bg-white mb-4 border")}>
          <div className="container max-w-[500px] flex flex-col items-center center">
            <span className="main-form-cards_title mb-0">Open Bank Account</span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[20px] instruction">
        <div className="px-5 py-5 bg-white border col-span-2 md:col-span-1 md:px-[60px] md:py-10">
          <Alert>
            <span className="font-bold">
              <FormattedMessage
                id="send-exact"
                values={{
                  amount,
                  currency: crypto,
                  b: (chunk) => <strong className="font-[ChakraPetchSemiBold]">{chunk}</strong>,
                }}
              />
            </span>
          </Alert>
          <div className="text-black text-[14px] font-bold mt-[20px]">
            <p>
              <FormattedMessage id="we-do-not" />
            </p>
            {isRainCard ? (
              <div>
                <p className="font-[ChakraPetchBold] my-3">Important Instructions:</p>
                <ul className="list-disc ml-4">
                  <li>This deposit request remains active for 15 minutes.</li>

                  <li>Please complete the transfer within the allocated time.</li>

                  <li>Network confirmation is required before funds are credited.</li>

                  <li>Once sufficient confirmations are received, the top-up will be added to your card balance automatically.</li>

                  <li>All incoming transactions are screened in line with AML and compliance requirements.</li>

                  <li>If an incorrect amount is sent or the transaction is delayed, please contact customer support.</li>
                </ul>
                <p className="my-3 font-[ChakraPetchBold]">Processing Time:</p>
                <p>Funds are typically credited after the required blockchain confirmations.</p>
              </div>
            ) : (
              <Fragment>
                {isOnrampUser && (
                  <div className="flex gap-[8px] mt-[15px]">
                    <OnRampIcon />
                    <p>
                      <FormattedMessage id="to-make" />
                    </p>
                  </div>
                )}
                <div>
                  <p className="my-[15px]">
                    <FormattedMessage id="please-read" />
                  </p>
                  <ul className="list-disc pl-3.5 child:pl-2 child:mb-2.5">
                    {isOnrampUser && (
                      <li>
                        <FormattedMessage id="rules1" />
                      </li>
                    )}
                    <li>
                      <FormattedMessage id="rules2" />
                    </li>
                    <li>
                      <FormattedMessage id="rules3" />
                      <br />
                      <FormattedMessage id="rules3.1" />
                    </li>
                    <li>
                      <FormattedMessage id="rules4" />
                    </li>
                  </ul>
                </div>
                <div className="mt-[15px]">
                  {!isOrderIban && (
                    <p>
                      <FormattedMessage id={isIban ? "rules5_iban" : "rules5"} />
                    </p>
                  )}
                  <p className="mt-[15px]">
                    <FormattedMessage id="rules6" />
                  </p>
                </div>
              </Fragment>
            )}
          </div>
        </div>
        <div className="bg-white border p-10 pb-10 md:p-10 pt-10">
          {isExactProvider && crypto.toLowerCase() === "usdc-base" && (
            <div className="instruction__notice text-black">
              <Alert severity="error" color="inherit" icon={<AlertTriangle className="flex-shrink-0" size={20} />}>
                <div>
                  <p className="text-md font-bold">
                    <FormattedMessage id="important_notice" />!
                  </p>
                  <FormattedMessage
                    id="important_notice_provider_4"
                    values={{
                      b: (chunk) => <strong className="font-[ChakraPetchSemiBold]">{chunk}</strong>,
                      br: <br />,
                    }}
                  />
                </div>
              </Alert>
            </div>
          )}
          {!isExactProvider && !isOrder && (
            <div className="instruction__notice text-black">
              <Alert severity="error" color="inherit">
                <div>
                  <p className="text-md font-bold">
                    <FormattedMessage id="important_notice" />!
                  </p>
                  <FormattedMessage id={`important_notice_content${isIban ? "_iban" : ""}`} />
                </div>
              </Alert>
            </div>
          )}
          <div className={cn("col-span-2 md:col-span-1 flex items-center justify-center", isOnrampUser && "items-start justify-start")}>
            {isOnrampUser ? (
              <div className="onramp-info">
                <h1>
                  <FormattedMessage id="tu-step2" />
                </h1>
                <p>
                  <FormattedMessage id="amout2" />:{" "}
                  <span>
                    {deposit} {fiat}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="spend" />:{" "}
                  <span>
                    {amount} {crypto}
                  </span>
                </p>
                <div className="flex gap-2">
                  <FormattedMessage id="warning" />:
                  <components.Timer
                    text={t({ id: "recalculated_in" })}
                    className="text-red-500 m-0"
                    enabled
                    format="MM:SS"
                    count_down={900}
                    onUpdate={fetchDepositAddress}
                    secText="min"
                  />
                </div>
              </div>
            ) : (
              <div className="instruction__info">
                {!loading ? (
                  <>
                    <img src={QRCode} className="instruction__info-qrcode" alt="" style={{ display: "inline-block" }} />
                    <div className="instruction__info-price">
                      {amount} {crypto}
                      <CopyIcon onClick={() => copyToClipboard(amount)} />
                    </div>
                    <div className="insctruction__info-amount">
                      {isRainCard && <span className="text-black">Equivalent card top-up:</span>} {deposit} {fiat}
                    </div>
                    <div className="instruction__info-wallet flex-col">
                      {wallet}
                      <CopyIcon onClick={() => copyToClipboard(wallet)} />
                    </div>
                    <div className="instruction__info-recalc">
                      <components.Timer
                        text={t({ id: "recalculated_in" })}
                        enabled={wallet != null}
                        format="MM:SS"
                        count_down={900}
                        onUpdate={fetchDepositAddress}
                        secText="min"
                      />
                    </div>
                    {crypto === "USDT" && (
                      <span className="note-tip">
                        <FormattedMessage id="note-tip" />
                      </span>
                    )}
                  </>
                ) : (
                  <h6>
                    <FormattedMessage id="loading" />
                    ...
                  </h6>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col-reverse justify-between my-5 md:my-10 md:flex-row gap-3 md:gap-0">
        <Button back variant="outlined" to="/">
          <FormattedMessage id="go-to-dashboard" />
        </Button>
        <div className="flex flex-col items-start md:flex-row md:items-center gap-2.5 w-full md:w-auto">
          <ul className="list-disc text-cl14 text-red-500 pl-3.5 md:p-0">
            {isOnrampUser ? (
              <li>
                <FormattedMessage id="you-will-redirect" />
              </li>
            ) : (
              <li>
                <FormattedMessage id="please-dont" />
              </li>
            )}
          </ul>
          <Button
            className="min-w-full md:min-w-[115px]"
            disabled={createLoading}
            onClick={() => {
              window.removeEventListener("blur", onleave)
              block.current = false
              createDeposit()
            }}
          >
            <FormattedMessage id={createLoading ? "loading" : isOnrampUser ? "proceed" : "i-have-paid"} />
          </Button>
        </div>
      </div>
    </>
  )
}

export default Instruction
